import React from 'react';

import { connect } from 'react-redux';

import { colors, type } from '@nerdwallet/base-styles/variables';
import { Box } from '@nerdwallet/currency';
import { Num as num } from '@nerdwallet/nw-client-lib';

import { SCHEDULE_DURATION } from '../../../../lib/rentvsbuy-constants';
import { UPDATE_CHART } from '../../../../reducers/rentvsbuy-calculator';
import { Browser } from '../../../../types';
import ResultsChart from '../../../results-chart/results-chart';
import { Outputs, Tooltip } from '../../types';

// Actions
import ResultsTooltip from './results-tooltip/results-tooltip';

// Styles
import styles from './results-chart.module.less';

const getMaxTotalNPV = (schedule) => {
  return Math.max(...schedule.map((yearData) => yearData.totalCostNPV));
};

const getFormattedOutput = (property, data) => {
  return num.formatPercent(data[property] ?? 0, '$0a');
};

type ResultChartProps = {
  outputs: Outputs;
  plannedStay: number;
  dispatch: (args: any) => void;
  browser: Browser;
  trackingFormName: string;
  tooltip?: Tooltip;
};

const RentVsBuyResultsChart = ({
  outputs,
  plannedStay,
  dispatch,
  browser,
  trackingFormName,
  tooltip,
}: ResultChartProps): JSX.Element => {
  const { rentingSchedule, buyingSchedule, breakevenYear } = outputs;
  const rentMaxTotal = getMaxTotalNPV(rentingSchedule);
  const buyMaxTotal = getMaxTotalNPV(buyingSchedule);
  const max = Math.round(Math.max(rentMaxTotal, buyMaxTotal) / 25000) * 25000;

  const values = {
    max,
    min: 0,
    half: Math.round(Math.round(max / 2) / 25000) * 25000,
  };

  const yMin = getFormattedOutput('min', values);
  const yHalf = getFormattedOutput('half', values);
  const yMax = getFormattedOutput('max', values);

  const rentDataSet = {
    name: 'Rent',
    data: rentingSchedule,
    x: 'year',
    y: 'totalCostNPV',
    style: { data: { stroke: colors.yellow } },
  };

  const buyDataSet = {
    name: 'Buy',
    data: buyingSchedule,
    x: 'year',
    y: 'totalCostNPV',
    style: { data: { stroke: colors.blue } },
  };

  let breakevenLabelPoint = Math.round((values.max + values.half) / 2);
  breakevenLabelPoint = Math.round((values.max + breakevenLabelPoint) / 2);

  const breakevenDataSet = {
    name: 'Breakeven',
    data: [
      { x: breakevenYear, y: values.min },
      {
        x: breakevenYear,
        y: breakevenLabelPoint,
        label: !browser.isMobile && breakevenYear !== null ? 'BREAK EVEN' : '',
      },
      { x: breakevenYear, y: values.max },
    ],
    x: 'x',
    y: 'y',
    style: {
      data: {
        stroke: colors['neutral-light'],
        strokeDasharray: '5, 5',
        strokeWidth: 1,
      },
      labels: {
        letterSpacing: 1.6,
        fill: colors['neutral-dark'],
        fontFamily: type['font-family-gotham'],
      },
    },
  };

  const cursorData = [
    { x: plannedStay, y: rentingSchedule[plannedStay - 1].totalCostNPV },
    { x: plannedStay, y: buyingSchedule[plannedStay - 1].totalCostNPV },
  ];

  const tracking = {
    formName: trackingFormName,
    fieldName: 'planned-stay',
  };
  return (
    <Box className={styles.resultChartWrapper} marginTop="2" id="chartWrapper">
      <ResultsChart
        browser={browser}
        data={[rentDataSet, buyDataSet, breakevenDataSet]}
        xLabel="YEARS"
        yLabel="TOTAL COST"
        chartWidth={browser.isMobile ? 450 : 700}
        domain={[0, SCHEDULE_DURATION]}
        yTickValues={[yMax, yHalf, yMin]}
        cursorData={cursorData}
        cursorDefaultX={plannedStay ?? 1}
        cursorYValues={{ min: values.min, max: values.max + 10000 }}
        cursorColor={colors.teal}
        dispatch={dispatch}
        action={UPDATE_CHART}
        tracking={tracking}
      />
      {tooltip.showTooltip && (
        <ResultsTooltip
          yRange={values.max}
          data={cursorData}
          chartData={tooltip.data}
        />
      )}
    </Box>
  );
};

const mapStateToProps = (state) => ({
  outputs: state?.rentvsbuyCalculator?.outputs,
  tooltip: state?.rentvsbuyCalculator?.tooltip,
  plannedStay: state?.rentvsbuyCalculator?.plannedStay,
  browser: state?.browser,
  identity: state?.identity,
});

export default connect(mapStateToProps)(RentVsBuyResultsChart);
